import { resolveApiBaseUrl } from "./apiBase";

const API_URL = resolveApiBaseUrl();

const getAuthToken = (): string | null => {
  const token = localStorage.getItem("idToken") || localStorage.getItem("authToken");
  return token ? token.trim() : null;
};

export type QuotaAlertLevel = "OK" | "INFO" | "WARN" | "CRITICAL" | "OVERFLOW_ACTIVE";

export type EmailQuotaPeriod = {
  period: string;
  used: number;
  hardCap: number;
  softWarn: number;
  warn: number;
  percent: number;
  providerReportedUsed?: number | null;
  driftDetected?: boolean;
  resetAt?: string;
  projected?: number;
};

export type EmailQuotaStatus = {
  provider: string;
  activeProvider?: string;
  alertLevel: QuotaAlertLevel;
  day: EmailQuotaPeriod;
  month: EmailQuotaPeriod;
  projectedMonth: number;
  overflowCountThisMonth: number;
  overflowActive: boolean;
  overflowToMailjet: boolean;
  mailjetPaidConfirmedAt?: string | null;
  hardCapBlocked?: boolean;
  config?: {
    thresholds: Record<string, number>;
    alertTo: string[];
    overflowToMailjet: boolean;
    mailjetPaidConfirmedAt?: string | null;
  };
};

export type EmailQuotaBanner = {
  level: QuotaAlertLevel;
  title: string;
  body: string;
  mailjetStarterSection: string;
};

export type EmailQuotaHistoryItem = {
  period: string;
  used: number;
  hardCap: number;
  providerReportedUsed?: number | null;
  driftDetected?: boolean;
};

export type EmailQuotaConfigUpdate = {
  thresholds?: Partial<Record<string, number>>;
  alertTo?: string[];
  overflowToMailjet?: boolean;
  mailjetStarterActive?: boolean;
  mailjetPaidConfirmedAt?: string | null;
};

async function authFetch(path: string, init?: RequestInit) {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("Authentication required");
  }
  const url = API_URL ? `${API_URL}${path}` : path;
  const response = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${authToken}`,
      ...(init?.headers || {}),
    },
  });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || `Request failed (${response.status})`);
  }
  return text ? JSON.parse(text) : {};
}

export async function fetchEmailQuotaStatus(): Promise<{
  status: EmailQuotaStatus;
  banner: EmailQuotaBanner | null;
}> {
  const data = await authFetch("/email/quota/status");
  if (!data.success) {
    throw new Error(data.message || "Failed to load quota status");
  }
  return { status: data.status, banner: data.banner ?? null };
}

export async function fetchEmailQuotaHistory(days = 30): Promise<EmailQuotaHistoryItem[]> {
  const data = await authFetch(`/email/quota/history?days=${days}`);
  if (!data.success) {
    throw new Error(data.message || "Failed to load quota history");
  }
  return Array.isArray(data.history) ? data.history : [];
}

export async function updateEmailQuotaConfig(
  payload: EmailQuotaConfigUpdate
): Promise<EmailQuotaStatus["config"]> {
  const data = await authFetch("/email/quota/config", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!data.success) {
    throw new Error(data.message || "Failed to update quota config");
  }
  return data.config;
}

export async function reconcileEmailQuota(): Promise<unknown> {
  const data = await authFetch("/email/quota/reconcile", { method: "POST" });
  if (!data.success) {
    throw new Error(data.message || "Failed to reconcile quota");
  }
  return data.result;
}
